import SelectField from "../../../../common/SelectField";

/**
 * Customizable variables panel (language, comms library, client, admin, support, network).
 *
 * @param {object} props
 * @param {object} props.form
 * @param {boolean} props.isPartnershipPlan
 * @param {ReadonlyArray<object>} props.networkNames
 * @param {Record<string, string | object>} props.validationErrors
 * @param {(field: string, value: *) => void} props.onFieldChange
 * @param {(optionListKey: string, title: string) => void} props.onSelectOption
 * @param {(networkTitle: string) => void} props.onNetworkSelect
 * @param {(value: string) => void} props.onNetworkOtherChange
 * @param {(value: string) => void} props.onPcpCopayOtherChange
 * @returns {import('react').JSX.Element}
 */
const CustomizableVariables = ({
    form,
    isPartnershipPlan,
    networkNames,
    validationErrors,
    onFieldChange,
    onSelectOption,
    onNetworkSelect,
    onNetworkOtherChange,
}) => {
    const languageOptions = form.languages.map((language) => ({
        value: language.title,
        label: language.title,
    }));
    const selectedLanguage =
        form.languages.find((language) => language.isSelected)?.title ??
        languageOptions[0]?.value;

    const selectedNetwork = networkNames.find((network) => network.isSelected);
    const networkError =
        typeof validationErrors.commsLibraryOptions === "object"
            ? validationErrors.commsLibraryOptions?.networkNames
            : undefined;

    return (
        <section className="plan-notification-panel plan-notification-panel--customizable">
            <h2 className="plan-notification-panel__title">Customizable variables</h2>
            <p className="plan-notification-panel__required">
                <span aria-hidden>*</span> All fields below are required
            </p>

            <div className="plan-notification-field">
                <span className="plan-notification-field__label">Language</span>
                <p className="plan-notification-field__helper">
                    All electronic communications are in English only. Paper
                    communications can be turned on in English + Spanish at the
                    plan level (not at the individual member level).
                </p>
                <div
                    className="plan-notification-language-radios"
                    role="radiogroup"
                    aria-label="Communication language"
                >
                    {languageOptions.map((option) => {
                        const optionId = `language-${option.value}`;

                        return (
                            <label
                                key={option.value}
                                className="plan-notification-language-radios__option"
                                htmlFor={optionId}
                            >
                                <input
                                    id={optionId}
                                    type="radio"
                                    name="notification-language"
                                    checked={selectedLanguage === option.value}
                                    onChange={() =>
                                        onSelectOption("languages", option.value)
                                    }
                                />
                                <span>{option.label}</span>
                            </label>
                        );
                    })}
                </div>
                {validationErrors.languages ? (
                    <p className="plan-notification-field__error" role="alert">
                        {validationErrors.languages}
                    </p>
                ) : null}
            </div>

            <SelectField
                id="comms-library"
                label="Comms Library"
                variant="notification"
                value={
                    form.commsLibraryOptions.find((option) => option.isSelected)
                        ?.title ?? ""
                }
                onChange={(value) =>
                    onSelectOption("commsLibraryOptions", value)
                }
                placeholderOption="Select a communications library"
                options={form.commsLibraryOptions.map((option) => ({
                    value: option.title,
                    label: option.title,
                    description: option.subtitle,
                }))}
                helperText="Choose which communications library applies for this plan."
                error={
                    typeof validationErrors.commsLibraryOptions === "string"
                        ? validationErrors.commsLibraryOptions
                        : undefined
                }
            />

            <div className="form-input">
                <label htmlFor="client-name">Client name</label>
                <input
                    id="client-name"
                    type="text"
                    placeholder="Enter external friendly client name"
                    value={form.clientName}
                    onChange={(event) =>
                        onFieldChange("clientName", event.target.value)
                    }
                />
                <p className="plan-notification-field__helper">
                    This is how the client&apos;s name will be displayed in member
                    communications.
                </p>
                {validationErrors.clientName ? (
                    <p className="plan-notification-field__error" role="alert">
                        {validationErrors.clientName}
                    </p>
                ) : null}
            </div>

            <SelectField
                id="administrator-name"
                label="Administrator Name"
                variant="notification"
                value={
                    form.administratorName.find((option) => option.isSelected)
                        ?.title ?? ""
                }
                onChange={(value) => onSelectOption("administratorName", value)}
                placeholderOption="Select an administrator"
                options={form.administratorName.map((option) => ({
                    value: option.title,
                    label: option.title,
                }))}
                helperText="This is the brand that the plan is being offered under and will display in communications."
                error={validationErrors.administratorName}
            />

            <SelectField
                id="support-team-name"
                label="Support team name"
                variant="notification"
                value={
                    form.supportTeamName.find((option) => option.isSelected)
                        ?.title ?? ""
                }
                onChange={(value) => onSelectOption("supportTeamName", value)}
                placeholderOption="Select a support team"
                options={form.supportTeamName.map((option) => ({
                    value: option.title,
                    label: option.title,
                }))}
                helperText="This is the name the client uses to refer to the member support team."
                error={validationErrors.supportTeamName}
            />

            <SelectField
                id="network-name"
                label="Network name"
                variant="notification"
                value={selectedNetwork?.title ?? ""}
                onChange={onNetworkSelect}
                placeholderOption="Select a network"
                options={networkNames.map((network) => ({
                    value: network.title,
                    label: network.title,
                }))}
                helperText="This is the name of the network that will appear in certain member communications."
                disabled={isPartnershipPlan}
                error={networkError}
            />

            {selectedNetwork?.title === "Other" && !isPartnershipPlan ? (
                <div className="form-input">
                    <label htmlFor="network-other">External network name</label>
                    <input
                        id="network-other"
                        type="text"
                        value={selectedNetwork.value ?? ""}
                        onChange={(event) =>
                            onNetworkOtherChange(event.target.value)
                        }
                    />
                </div>
            ) : null}
        </section>
    );
};

export default CustomizableVariables;
